import {
    ReactElement,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import { captureException } from '@sentry/browser'
import { message } from 'antd'
import type { AxiosError } from 'axios'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { fetchUserSettingsSuccess } from 'actions/auth'
import {
    downloadReport,
    toggleReportsAppDrawer,
    unmountAppDrawer,
} from 'actions/ui/app'
import { CerebroPaginatedResponse } from 'api/Cerebro/types'
import {
    updateUserSettings,
    type UserSettings,
} from 'api/Cerebro/unscoped/user'
import { useAppConfig } from 'appConfig'
import { AppBreadcrumb } from 'components/AppBreadcrumb'
import { date } from 'configuration/dayjs'
import { SegmentEventName } from 'const/monitoring/segment'
import { usePageContext } from 'context/PageContext'
import { fetchTriggeredAlertsForDrawer } from 'features/App/service/appData'
import { useAnalytics } from 'features/Monitoring/Segment/useAnalytics'
import { useReportsDrawerQuery } from 'features/Reports/api/reportsDrawerQuery'
import { userHasDashboardsOnlyPermissions } from 'helpers/featurePermissions'
import { isReportPending } from 'helpers/reports'
import {
    useAction,
    useAuthDomainValue,
    useCerebroApiRequest,
    usePollingWhilePending,
    useUserHasPermissions,
} from 'hooks'
import useElementSize from 'hooks/useElementSize'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useGatedFeatures } from 'hooks/useGatedFeatures'
import {
    selectHasRecentIntegration,
    selectUserOrganizationFeaturePermissions,
} from 'selectors/auth'
import { selectCurrentOrganization } from 'selectors/orgs'
import { selectGlobalNotification } from 'selectors/ui'
import {
    Breadcrumb,
    GlobalNotification,
    Organization,
    Report,
    RootReduxState,
    UserPermission,
} from 'types'
import {
    GlobalNotificationBanner,
    IntegrationIssuesBanner,
    RecentIntegrationBanner,
    TrialNotificationBanner,
    UserFeedbackBanner,
} from 'views/AppLayout/Banners'

import { AlertsDrawer } from './Drawers/AlertsDrawer'
import { filterWhatsNewEntries } from './Drawers/filterWhatsNewEntries'
import { ReportsDrawer } from './Drawers/ReportsDrawer'
import { getWhatsNewEntries } from './Drawers/whatsNewContent'
import { WhatsNewDrawer } from './Drawers/WhatsNewDrawer'
import { AlertsNavItem } from './NavItems/AlertsNavItem'
import { GetHelpNavItem } from './NavItems/GetHelpNavItem'
import { ImpersonationModeNavItem } from './NavItems/ImpersonationModeNavItem'
import { InsightsNavItem } from './NavItems/InsightsNavItem'
import { ProfileNavItem } from './NavItems/ProfileNavItem'
import { ReportsNavItem } from './NavItems/ReportsNavItem'
import { WhatsNewNavItem } from './NavItems/WhatsNewNavItem'
import StickyHeader from './StickyHeader'
import * as styles from './styles.module.scss'

type NavItemKey =
    | 'alerts'
    | 'help'
    | 'impersonation'
    | 'insights'
    | 'profile'
    | 'reports'
    | 'whatsNew'

interface PageHeaderProps {
    elevated?: boolean
    color?: 'transparent' | 'white'
    breadcrumbs?: Breadcrumb[]
    titleElement?: ReactElement
    filterGroupElement?: ReactElement
    stickyHeaderElement?: ReactElement
    stickyHeaderThresholdRef?: RefObject<HTMLDivElement>
    filterOverlay?: string
    filterClassName?: string
    stickyHeaderHoverOverflow?: boolean
}

const PageHeader = ({
    elevated = false,
    color = 'white',
    filterGroupElement,
    stickyHeaderElement,
    stickyHeaderThresholdRef,
    breadcrumbs = [],
    titleElement,
    filterOverlay,
    filterClassName,
    stickyHeaderHoverOverflow = false,
}: PageHeaderProps): ReactElement => {
    const { isChinaBuild } = useAppConfig()
    const { t } = useTranslation(['navigation', 'account'])
    const { trackEvent } = useAnalytics()
    const featureFlags = useFeatureFlags()
    const gatedFeatures = useGatedFeatures()

    const dashboardOnlyExperience = useUserHasPermissions(
        userHasDashboardsOnlyPermissions
    )

    const globalNotification = useSelector<
        RootReduxState,
        GlobalNotification | null | undefined
    >((state: RootReduxState) => selectGlobalNotification(state))
    const hasRecentIntegration = useSelector<RootReduxState, boolean>(
        (state: RootReduxState) => selectHasRecentIntegration(state)
    )
    const currentOrganization = useSelector<
        RootReduxState,
        Organization | undefined
    >((state) => selectCurrentOrganization(state))
    const featurePermissions = useSelector<RootReduxState, UserPermission[]>(
        (state) => selectUserOrganizationFeaturePermissions(state)
    )

    const organizationLockedToolbar = useMemo(
        () =>
            Boolean(
                currentOrganization &&
                    (currentOrganization.locked === true ||
                        currentOrganization.enabled === false)
            ),
        [currentOrganization]
    )

    const unmountDrawer = useAction(unmountAppDrawer)
    const dispatchDownloadReport = useAction<{ reportId: string }>(
        downloadReport
    )
    const dispatchUserSettingsSuccess = useAction<UserSettings>(
        fetchUserSettingsSuccess
    )
    const makeCerebroApiRequest = useCerebroApiRequest()
    const filtersRef = useRef<HTMLDivElement>(null)

    // Report drawer state management
    const newReports = useSelector(
        (state: RootReduxState) => state.ui.app.newReports
    )
    const isReportsDrawerOpen = useSelector(
        (state: RootReduxState) => state.ui.app.drawerReports.isVisible
    )
    const toggleReportsDrawer = useAction(toggleReportsAppDrawer)

    const {
        refetchInterval,
        pollTimeoutReached,
        reset: resetReportsPolling,
    } = usePollingWhilePending<
        Report,
        CerebroPaginatedResponse<Report> | null,
        AxiosError
    >({
        getResults: (d) => d?.data?.results ?? [],
        isPending: isReportPending,
    })

    const { data: drawerReportsData, isFetching: drawerReportsLoading } =
        useReportsDrawerQuery({
            enabled: isReportsDrawerOpen,
            options: {
                refetchInterval: isReportsDrawerOpen ? refetchInterval : false,
            },
        })

    const drawerReports = drawerReportsData?.data.results ?? []

    const hasShownDrawerTimeoutRef = useRef(false)
    useEffect(() => {
        if (!pollTimeoutReached || !isReportsDrawerOpen) {
            return
        }
        if (hasShownDrawerTimeoutRef.current) {
            return
        }
        hasShownDrawerTimeoutRef.current = true

        const error = {
            message:
                'This drawer has stopped checking for updates. Re-open the drawer to start checking for updates again.',
        }
        captureException(error)

        const closeMessage = message.warning(error.message, 0)
        return () => {
            closeMessage()
        }
    }, [isReportsDrawerOpen, pollTimeoutReached])

    useEffect(() => {
        if (!isReportsDrawerOpen) {
            hasShownDrawerTimeoutRef.current = false
            // Without this, a stuck pending row leaves pollTimeoutReached and
            // pollingStartedAtRef set, so reopening the drawer immediately
            // re-times out — the "Re-open the drawer" hint can't recover.
            resetReportsPolling()
        }
    }, [isReportsDrawerOpen, resetReportsPolling])

    const handleClickReportsButton: () => void = useCallback(() => {
        toggleReportsDrawer(!isReportsDrawerOpen)
    }, [isReportsDrawerOpen, toggleReportsDrawer])
    const handleCloseReportsDrawer = (): void => {
        unmountDrawer()
        toggleReportsDrawer(false)
    }

    // Alerts drawer state management
    const triggeredAlerts = useSelector(
        (state: RootReduxState) => state.ui.app.triggeredAlerts.results
    )
    const [isAlertsDrawerOpen, setIsAlertsDrawerOpen] = useState(false)

    const handleClickAlertsButton: () => void = useCallback(() => {
        setIsAlertsDrawerOpen((open) => {
            if (!open) {
                fetchTriggeredAlertsForDrawer()
            }
            return !open
        })
    }, [])
    const handleCloseAlertsDrawer = (): void => {
        unmountDrawer()
        setIsAlertsDrawerOpen(false)
    }

    // What's New drawer state management
    const [isWhatsNewDrawerOpen, setIsWhatsNewDrawerOpen] = useState(false)
    const latestSeenWhatsNewEntryId = useAuthDomainValue<string | null>([
        'settings',
        'latestNewFeatureId',
    ])
    const whatsNewEntries = useMemo(
        () =>
            filterWhatsNewEntries(
                getWhatsNewEntries(t),
                featureFlags,
                gatedFeatures,
                featurePermissions
            ),
        [featureFlags, gatedFeatures, featurePermissions, t]
    )
    const latestWhatsNewEntryId = whatsNewEntries[0]?.id
    const hasUnseenWhatsNewEntry = Boolean(
        latestWhatsNewEntryId &&
            latestSeenWhatsNewEntryId !== latestWhatsNewEntryId
    )

    const handleClickWhatsNewButton: () => void = useCallback(() => {
        if (!isWhatsNewDrawerOpen) {
            trackEvent(SegmentEventName.WHATS_NEW_OPENED, {
                location: 'Top Nav',
                text: "What's New",
                type: 'Button',
                hasUnseenEntry: hasUnseenWhatsNewEntry,
                latestEntryId: latestWhatsNewEntryId,
            })
        }

        if (
            !isWhatsNewDrawerOpen &&
            latestWhatsNewEntryId &&
            hasUnseenWhatsNewEntry
        ) {
            const newSetting: UserSettings = {
                latestNewFeatureId: latestWhatsNewEntryId,
                lastUpdated: date(),
            }

            void makeCerebroApiRequest({
                request: updateUserSettings(newSetting),
                onRequestSuccess: () => dispatchUserSettingsSuccess(newSetting),
                suppressErrorAlerts: true,
            })
        }

        setIsWhatsNewDrawerOpen((open) => !open)
    }, [
        dispatchUserSettingsSuccess,
        hasUnseenWhatsNewEntry,
        isWhatsNewDrawerOpen,
        latestWhatsNewEntryId,
        makeCerebroApiRequest,
        trackEvent,
    ])
    const handleCloseWhatsNewDrawer = (): void => {
        setIsWhatsNewDrawerOpen(false)
    }

    // Sticky header
    const renderStickyHeader = (): ReactElement | null => {
        const thresholdElement = (stickyHeaderThresholdRef || filtersRef)
            .current

        if (!stickyHeaderElement || !thresholdElement) {
            return null
        }

        const scrollTop = thresholdElement.offsetTop
        return (
            <StickyHeader
                topOffset={scrollTop}
                hoverOverflow={stickyHeaderHoverOverflow}
            >
                {stickyHeaderElement}
            </StickyHeader>
        )
    }

    const headerRef = useRef<HTMLDivElement>(null)
    const headerSize = useElementSize<HTMLDivElement>(headerRef)
    const { setHeaderSize } = usePageContext()

    useEffect(() => {
        setHeaderSize(headerSize)
    }, [headerSize, setHeaderSize])

    const navItems: Record<NavItemKey, ReactElement | null> = useMemo(
        () => ({
            alerts: dashboardOnlyExperience ? null : (
                <AlertsNavItem onClick={handleClickAlertsButton} />
            ),
            help: isChinaBuild ? null : (
                <GetHelpNavItem disabled={organizationLockedToolbar} />
            ),
            impersonation: <ImpersonationModeNavItem />,
            insights: dashboardOnlyExperience ? null : <InsightsNavItem />,
            profile: (
                <ProfileNavItem
                    disableSettingsNavigation={organizationLockedToolbar}
                />
            ),
            reports: <ReportsNavItem onClick={handleClickReportsButton} />,
            whatsNew: (
                <WhatsNewNavItem
                    hasUnseenEntry={hasUnseenWhatsNewEntry}
                    onClick={handleClickWhatsNewButton}
                />
            ),
        }),
        [
            dashboardOnlyExperience,
            handleClickAlertsButton,
            handleClickWhatsNewButton,
            hasUnseenWhatsNewEntry,
            isChinaBuild,
            handleClickReportsButton,
            organizationLockedToolbar,
        ]
    )

    const navItemsToDisplay: NavItemKey[] = organizationLockedToolbar
        ? ['help', 'profile']
        : [
              'impersonation',
              'help',
              'whatsNew',
              'alerts',
              'insights',
              'reports',
              'profile',
          ]

    const navList = (
        <div>
            <ul className={styles.headerNav}>
                {navItemsToDisplay.map((itemKey) => {
                    const item = navItems[itemKey]
                    return item && <li key={itemKey}>{item}</li>
                })}
            </ul>
        </div>
    )

    const headerBarRow = organizationLockedToolbar ? (
        <nav
            aria-label={t(
                'account:OrganizationLockedPage.accountNavAriaLabel',
                'Account actions'
            )}
            className={styles.toolbarNavLandmark}
        >
            <div
                className={classNames(
                    styles.pageHeaderBar,
                    styles.pageHeaderBarAccountOnly,
                    styles.pageHeaderBarLockedToolbarShell
                )}
            >
                {navList}
            </div>
        </nav>
    ) : (
        <div className={styles.pageHeaderBar}>
            <AppBreadcrumb items={breadcrumbs} />
            {navList}
        </div>
    )

    return (
        <div
            ref={headerRef}
            className={styles.pageHeaderRoot}
            data-testid="page-header"
        >
            {!organizationLockedToolbar && (
                <>
                    <GlobalNotificationBanner
                        globalNotification={globalNotification}
                    />
                    <RecentIntegrationBanner
                        hasRecentIntegration={hasRecentIntegration}
                    />
                    <TrialNotificationBanner
                        organization={currentOrganization}
                    />
                    <UserFeedbackBanner />
                    <IntegrationIssuesBanner />
                </>
            )}
            <div
                className={classNames(styles.wrapper, {
                    [styles.wrapperElevated]:
                        elevated && !organizationLockedToolbar,
                    [styles.wrapperWhite]: color === 'white',
                })}
            >
                {headerBarRow}
                {!organizationLockedToolbar && titleElement && (
                    <div className={styles.titleRow}>{titleElement}</div>
                )}
                {!organizationLockedToolbar && filterGroupElement && (
                    <div
                        ref={filtersRef}
                        className={classNames(
                            styles.filtersWrap,
                            filterClassName
                        )}
                    >
                        {filterOverlay && (
                            <div className={styles.filtersOverlay}>
                                {filterOverlay}
                            </div>
                        )}
                        {filterGroupElement}
                    </div>
                )}
            </div>
            {renderStickyHeader()}
            {!organizationLockedToolbar && (
                <>
                    <AlertsDrawer
                        triggeredAlerts={triggeredAlerts}
                        visible={isAlertsDrawerOpen}
                        onClose={handleCloseAlertsDrawer}
                    />
                    <ReportsDrawer
                        newReports={newReports}
                        drawerReports={drawerReports}
                        drawerReportsLoading={drawerReportsLoading}
                        downloadReport={dispatchDownloadReport}
                        visible={isReportsDrawerOpen}
                        onClose={handleCloseReportsDrawer}
                    />
                    <WhatsNewDrawer
                        entries={whatsNewEntries}
                        visible={isWhatsNewDrawerOpen}
                        onClose={handleCloseWhatsNewDrawer}
                    />
                </>
            )}
        </div>
    )
}

export default PageHeader
